import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Home } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { cn } from '@/utils/cn';
import { APP_HOME_PATH, canGoBackInApp } from '@/pages/notFound';

function NotFoundIllustration({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 240 200"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden
    >
      <g className="text-foreground-subtle" stroke="currentColor" fill="none">
        <path d="M22 32l9 9M31 32l-9 9" strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="214" cy="40" r="5.5" strokeWidth="1.5" />
        <path d="M28 168l7 7M35 168l-7 7" strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="48" cy="178" r="3" strokeWidth="1.25" />

        <rect x="42" y="24" width="148" height="118" rx="10" strokeWidth="1.75" />
        <circle cx="58" cy="42" r="3.5" strokeWidth="1.25" />
        <circle cx="72" cy="42" r="3.5" strokeWidth="1.25" />
        <circle cx="86" cy="42" r="3.5" strokeWidth="1.25" />
        <path d="M42 56h148" strokeWidth="1.5" />

        <rect
          x="74"
          y="74"
          width="84"
          height="50"
          rx="6"
          strokeWidth="1.5"
          strokeDasharray="5 4"
        />
        <path d="M100 88v14M132 88v14" strokeWidth="2.25" strokeLinecap="round" />
        <path d="M104 114c7-8 25-8 32 0" strokeWidth="2" strokeLinecap="round" />

        <circle cx="176" cy="150" r="24" strokeWidth="2" />
        <path d="M193 167l20 20" strokeWidth="2.5" strokeLinecap="round" />
      </g>
    </svg>
  );
}

export function NotFoundPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const goBack = () => {
    if (canGoBackInApp(location.key)) {
      navigate(-1);
      return;
    }
    navigate(APP_HOME_PATH, { replace: true });
  };

  return (
    <Card className="w-full max-w-[720px] shadow-md">
      <div className="grid items-center gap-8 p-8 sm:p-10 md:grid-cols-[minmax(0,1fr)_minmax(160px,220px)] lg:gap-10 lg:px-12 lg:py-11">
        <div className="min-w-0">
          <p className="font-display text-[52px] font-semibold leading-none tracking-tight text-foreground sm:text-[64px]">
            404
          </p>
          <h1 className="mt-3 font-display text-headline-xl text-foreground">Page not found</h1>
          <p className="mt-3 max-w-md text-body-lg text-foreground-muted">
            The page you’re looking for doesn’t exist or has been moved.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-3 border-t border-border pt-6">
            <Link
              to={APP_HOME_PATH}
              className={cn(
                'inline-flex h-10 items-center justify-center gap-1.5 rounded-lg bg-brand px-4',
                'text-body-md font-semibold text-brand-foreground transition-colors hover:bg-brand-hover'
              )}
            >
              <Home className="h-4 w-4" strokeWidth={1.75} aria-hidden />
              Go to Home
            </Link>
            <button
              type="button"
              className="text-body-md font-medium text-foreground-muted transition-colors hover:text-foreground"
              onClick={goBack}
            >
              Go back
            </button>
          </div>
        </div>
        <NotFoundIllustration className="mx-auto w-full max-w-[220px] justify-self-end md:max-w-none" />
      </div>
    </Card>
  );
}